import { useState, type RefObject } from 'react';
import {
  findPushPlatform,
  readFileAsText,
  type PushPlatform,
  type PushPlatformDetails,
  type PushProvider,
} from '@/features/push-credentials';
import type { PushSaveContext } from '../types';
import { pushSaveContext, type PushCredentialWrites } from './push-credential-writes';
import { useSaveAttempt } from './use-save-attempt';

type FcmWriteInput = {
  writes: PushCredentialWrites;
  providers: readonly PushProvider[];
  platform: PushPlatform;
  iosProvider: PushProvider | null;
};

/** The Apple key goes first, because the two cannot both claim iOS. */
async function writeIosOnStoredKey({ writes, providers }: FcmWriteInput): Promise<void> {
  if (providers.includes('apns')) {
    await writes.remove('push_apns');
  }
  await writes.setConfig('push_fcm', 'allow_ios');
}

async function writeUploadedKey({ writes, providers, platform, iosProvider }: FcmWriteInput, file: File) {
  const allowIos = platform === 'ios' || iosProvider === 'fcm';
  const password = await readFileAsText(file);
  if (allowIos && providers.includes('apns')) {
    await writes.remove('push_apns');
  }
  await writes.upsert('push_fcm', { username: file.name, password, config: allowIos ? 'allow_ios' : '' });
}

/** Offering iOS the key Android already uses makes turning iOS on a permission change, not an upload. */
function isStoredKeyOfferedToIos(platform: PushPlatform, isEnabled: boolean, iosProvider: PushProvider | null) {
  return platform === 'ios' && isEnabled && iosProvider !== 'fcm';
}

export type UseFcmDraftOptions = {
  platform: PushPlatform;
  platforms: readonly PushPlatformDetails[];
  providers: readonly PushProvider[];
  isEnabled: boolean;
  isEditing: boolean;
  writes: PushCredentialWrites;
};

export type FcmDraft = {
  file: File | null;
  setFile: (file: File) => void;
  clearFile: () => void;
  isStoredKeyOffer: boolean;
  isDirty: boolean;
  hasKeyToSave: boolean;
  /** Shown once a save has been refused, and only while there is still no key. */
  fileError: string | null;
  fieldsRef: RefObject<HTMLDivElement | null>;
  refuse: () => void;
  getCredentials: (() => Promise<string>) | null;
  prepareSave: () => { context: PushSaveContext; write: () => Promise<void> };
};

export function useFcmDraft({
  platform,
  platforms,
  providers,
  isEnabled,
  isEditing,
  writes,
}: UseFcmDraftOptions): FcmDraft {
  const [file, setFile] = useState<File | null>(null);
  const attempt = useSaveAttempt();
  const iosProvider = findPushPlatform(platforms, 'ios')?.provider ?? null;
  const isStoredKeyOffer = isStoredKeyOfferedToIos(platform, isEnabled, iosProvider);
  const hasKeyToSave = isStoredKeyOffer || file != null;
  const hasUnsavedKey = !isEnabled && file != null;

  const prepareSave = () => {
    const input: FcmWriteInput = { writes, providers, platform, iosProvider };
    const staged = file;
    return {
      context: pushSaveContext(platforms, platform),
      write: () => (staged == null ? writeIosOnStoredKey(input) : writeUploadedKey(input, staged)),
    };
  };

  return {
    file,
    setFile,
    clearFile: () => {
      setFile(null);
      attempt.reset();
    },
    isStoredKeyOffer,
    isDirty: file != null || (isEditing && isStoredKeyOffer),
    hasKeyToSave,
    fileError: attempt.hasAttempted && !hasKeyToSave ? 'Upload a .json server key.' : null,
    fieldsRef: attempt.fieldsRef,
    refuse: attempt.refuse,
    getCredentials: hasUnsavedKey ? () => readFileAsText(file) : null,
    prepareSave,
  };
}
